import type { Me, MfaSetup } from '@bk/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { useEffect, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../lib/api';
import { Button, Card, ErrorText, Field } from '../ui';
import { dateTime } from '../lib/format';

interface Connections {
  server_url: string;
  connections: Array<{
    session_id: string;
    client_name: string;
    /** null = tutto l'account. */
    tenant_name: string | null;
    created_at: string;
    last_used_at: string | null;
  }>;
}

/** Assistenti AI collegati via MCP: indirizzo da aggiungere in Claude/ChatGPT e collegamenti revocabili. */
export function AssistantsCard() {
  const queryClient = useQueryClient();
  const data = useQuery({
    queryKey: ['mcp-connections'],
    queryFn: () => api<Connections>('/mcp/connections'),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api<void>(`/mcp/connections/${id}/revoke`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['mcp-connections'] }),
  });
  const [copied, setCopied] = useState(false);
  return (
    <Card className="max-w-xl">
      <h2 className="text-base font-semibold">Assistenti AI</h2>
      <p className="mb-4 text-sm text-ink/60">
        Collega Claude (o un altro assistente compatibile MCP) per fare da lì tutto ciò che fai qui:
        creare e configurare aziende, gestire persone, processi e proposte, svolgere i tuoi compiti.
      </p>
      {data.data && (
        <>
          <div className="mb-3 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded bg-black/[0.04] px-2 py-1.5 text-xs">
              {data.data.server_url}
            </code>
            <Button
              variant="ghost"
              onClick={() => {
                void navigator.clipboard?.writeText(data.data!.server_url);
                setCopied(true);
              }}
            >
              {copied ? 'Copiato' : 'Copia'}
            </Button>
          </div>
          <ol className="mb-4 list-inside list-decimal space-y-1 text-sm text-ink/70">
            <li>In Claude apri Impostazioni → Connettori → Aggiungi connettore personalizzato.</li>
            <li>Incolla l’indirizzo qui sopra e conferma.</li>
            <li>Si apre questa app: consenti l’accesso (vale per tutte le tue aziende).</li>
            <li>In chat chiedi di configurare la tua azienda, poi ogni giorno “cosa devo fare oggi?”.</li>
          </ol>
          <h3 className="mb-1 text-sm font-semibold">Collegati</h3>
          {data.data.connections.length === 0 && (
            <p className="text-sm text-ink/50">Nessun assistente collegato.</p>
          )}
          <ul className="space-y-2">
            {data.data.connections.map((c) => (
              <li key={c.session_id} className="flex items-center gap-2 text-sm">
                <span className="min-w-0 flex-1">
                  <span className="font-medium">{c.client_name}</span> · {c.tenant_name ?? 'tutto l’account'}
                  <span className="block text-xs text-ink/45">
                    collegato {dateTime(c.created_at)}
                    {c.last_used_at && ` · ultimo uso ${dateTime(c.last_used_at)}`}
                  </span>
                </span>
                <Button variant="ghost" onClick={() => revoke.mutate(c.session_id)}>
                  Revoca
                </Button>
              </li>
            ))}
          </ul>
        </>
      )}
      <ErrorText>
        {data.error ? errorMessage(data.error) : revoke.error ? errorMessage(revoke.error) : ''}
      </ErrorText>
    </Card>
  );
}

export function AccountPage({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const [setup, setSetup] = useState<MfaSetup | null>(null);
  const [qr, setQr] = useState('');
  const [otp, setOtp] = useState('');

  useEffect(() => {
    if (setup) void QRCode.toDataURL(setup.otpauth_url, { margin: 1, width: 200 }).then(setQr);
  }, [setup]);

  const done = () => {
    setSetup(null);
    setOtp('');
    void queryClient.invalidateQueries({ queryKey: ['me'] });
  };
  const start = useMutation({
    mutationFn: () => api<MfaSetup>('/auth/mfa/setup', { method: 'POST' }),
    onSuccess: setSetup,
  });
  const enable = useMutation({
    mutationFn: () => api<void>('/auth/mfa/enable', { body: { otp } }),
    onSuccess: done,
  });
  const disable = useMutation({
    mutationFn: () => api<void>('/auth/mfa/disable', { body: { otp } }),
    onSuccess: done,
  });
  const error = start.error ?? enable.error ?? disable.error;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    (me.mfa_enabled ? disable : enable).mutate();
  };

  return (
    <div className="space-y-6">
      <Card className="max-w-xl">
        <h2 className="text-base font-semibold">Account e sicurezza</h2>
        <p className="mb-6 text-sm text-ink/60">
          {me.name} · {me.email}
        </p>
        <h3 className="mb-1 text-sm font-semibold">Verifica in due passaggi</h3>
        <p className="mb-4 text-sm text-ink/60">
          {me.mfa_enabled
            ? 'Attiva: all’accesso viene chiesto il codice dell’app di autenticazione.'
            : 'Non attiva. Si consiglia di attivarla.'}
        </p>
        {!me.mfa_enabled && !setup && (
          <Button onClick={() => start.mutate()} disabled={start.isPending}>
            Attiva
          </Button>
        )}
        {setup && (
          <div className="mb-4 flex flex-wrap items-start gap-6">
            {qr && (
              <img
                src={qr}
                alt="Codice QR per l'app di autenticazione"
                className="h-40 w-40 rounded-lg border border-black/10"
              />
            )}
            <div className="min-w-0 flex-1 text-sm text-ink/70">
              <p className="mb-2">
                Inquadra il codice con l’app di autenticazione (Google Authenticator, 1Password,
                Authy…), poi inserisci il codice di 6 cifre.
              </p>
              <p className="break-all text-xs text-ink/50">Inserimento manuale: {setup.secret}</p>
            </div>
          </div>
        )}
        {(setup || me.mfa_enabled) && (
          <form className="flex flex-wrap items-end gap-3" onSubmit={submit}>
            <div className="w-40">
              <Field
                label="Codice"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                autoComplete="one-time-code"
              />
            </div>
            <Button
              type="submit"
              variant={me.mfa_enabled ? 'ghost' : 'primary'}
              disabled={enable.isPending || disable.isPending}
            >
              {me.mfa_enabled ? 'Disattiva' : 'Conferma'}
            </Button>
          </form>
        )}
        <div className="mt-4">
          <ErrorText>{error ? errorMessage(error) : ''}</ErrorText>
        </div>
      </Card>
    </div>
  );
}
